import Layout from "../Layout";
import { Users, Map, Unlock } from "lucide-react";

const QUICK_FACTS = [
  { label: "Based in", value: "Hong Kong & London" },
  { label: "What we do", value: "AWS cost savings via Terraform PRs" },
  { label: "How we work", value: "Engineering-led, product-driven" },
  { label: "Who uses us", value: "Teams already on AWS, Terraform, GitHub" },
] as const;

const HOW_WE_WORK_ITEMS = [
  {
    icon: Users,
    title: "You talk to the builders",
    description:
      "Questions and feedback go straight to the engineers building Infralyst, not a separate account layer or ticket queue.",
  },
  {
    icon: Map,
    title: "Roadmap driven by real use",
    description:
      "We prioritize work that makes infrastructure changes safer and easier for existing teams. If something would save your team time or money, we want to hear about it.",
  },
  {
    icon: Unlock,
    title: "No lock-in",
    description:
      "Every workspace starts on the Free plan with 3 PRs. If Infralyst isn't a fit, you can disconnect AWS and GitHub at any time.",
  },
] as const;

function AboutHero(): React.ReactElement {
  return (
    <section className="px-4 py-16 pt-40">
      <div className="max-w-4xl mx-auto text-center">
        <h1 className="mb-4 text-5xl font-bold leading-tight tracking-tight md:text-6xl">
          About <span className="text-primary">Infralyst</span>
        </h1>
        <p className="max-w-2xl mx-auto text-xl leading-relaxed text-surface-foreground/80">
          Infralyst is an independent, engineering-led company that helps teams
          cut AWS waste with reviewable Terraform PRs, not more dashboards.
        </p>
      </div>
    </section>
  );
}

function WhoWeAre(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <h2 className="text-4xl font-bold mb-6">Who we are</h2>
            <p className="text-lg leading-relaxed text-surface-foreground/80 mb-4">
              <strong>
                Built by engineers who kept seeing the same waste.
              </strong>{" "}
              Infralyst is built by engineers working between Hong Kong and
              London. Our background includes infrastructure and platform work
              at companies like Deliveroo and Starling Bank, with product input
              from advisors who've worked on security and reliability at Meta
              and Darktrace.
            </p>
            <p className="text-lg leading-relaxed text-surface-foreground/80">
              <strong>Opinionated about how infra tools should behave.</strong>{" "}
              We're deliberately small and focused: conservative changes, clear
              diffs, and tools that plug straight into Terraform and GitHub.
              Infralyst is built for teams who already treat infrastructure as
              code and want cost savings that respect that.
            </p>
          </div>
          <div>
            <h3 className="text-2xl font-bold mb-6">Quick facts</h3>
            <ul className="space-y-4">
              {QUICK_FACTS.map(({ label, value }) => (
                <li key={label} className="flex flex-col">
                  <span className="text-sm font-semibold text-surface-foreground/60">
                    {label}
                  </span>
                  <span className="text-lg text-surface-foreground/80">
                    {value}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhyWeBuiltThis(): React.ReactElement {
  return (
    <section id="why" className="scroll-mt-24 py-16 px-4">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-4xl font-bold mb-8">Why we built Infralyst</h2>
        <div className="space-y-4 text-lg leading-relaxed text-surface-foreground/80">
          <p>
            Cloud providers benefit when you overpay. The complexity isn't an
            accident. It's how the incentives work. And the tools they build to
            help you reduce costs will always be half-measures, because every
            dollar you save is a dollar off their top line.
          </p>
          <p>
            Teams try to manage it themselves. It never sticks. People are
            shipping features and handling incidents, so costs drift until
            someone panics about the bill. We went through that cycle enough
            times to think: this shouldn't be a process at all. It should run in
            the background, like monitoring or alerting. That's what Infralyst
            does.
          </p>
          <p>
            We use AI to read your Terraform, understand the context, and write
            the actual code change, so the PR is ready to review, not a
            suggestion you still have to implement.
          </p>
          <a
            href="/articles/why-we-built-infralyst"
            className="text-primary hover:underline transition-colors"
          >
            Read why we built it →
          </a>
        </div>
      </div>
    </section>
  );
}

function HowWeWork(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-12">
          How we work with customers
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {HOW_WE_WORK_ITEMS.map(({ icon: Icon, title, description }) => (
            <div
              key={title}
              className="p-6 rounded-3xl border bg-background/50 hover:border-primary/20 hover:bg-primary/5 transition-colors"
            >
              <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                <Icon className="size-6" />
              </div>
              <h3 className="text-lg font-bold mb-3">{title}</h3>
              <p className="text-surface-foreground/80 text-sm leading-relaxed">
                {description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Show(): React.ReactElement {
  return (
    <Layout
      title="About"
      description="Infralyst is an independent, engineering-led AWS cost optimization tool. We analyze utilization data and generate reviewable Terraform PRs to cut costs."
    >
      <AboutHero />
      <WhoWeAre />
      <WhyWeBuiltThis />
      <HowWeWork />
    </Layout>
  );
}
